import { AppImage, Icon, SkeletonLoader } from '@/components';
import { certificationBadgesVariants } from './UserBadges.styles';
import { cn, consoleLog } from '@/utils';
import { IUserBadge } from '@/types';
import { SIMULATION_LABELS } from '@/constants';
import { useGlobalStore } from '@/store';

interface UserBadgesProps {
  /**
   * Array of user certification badges
   */
  badges?: IUserBadge[];

  /**
   * Optional additional class names for styling
   */
  className?: string;
  
  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean;
}

/**
 * A component to display user certification badges. Shows a placeholder
 * message when no badges are earned, or displays earned certification badges
 * with names and icons.
 */
export const UserBadges: React.FC<UserBadgesProps> = ({
  className,
  badges = [],
  isLoading = false,
}) => {
  const { certifications, setActiveCertificate } = useGlobalStore();

  /**
   * Handler for when a certification badge is clicked. This could be used to
   * navigate to a certification details page or open a modal with more
   * information about the certification.
   */
  function handleViewCertificate(certId: number): void {
    const certification = certifications.find((c) => c.id === certId);
    if (certification) {
      setActiveCertificate(certification);
    } else {
      consoleLog.warn(`Certification with ID ${certId} not found.`);
    }
  }

  return (
    <div className={cn(certificationBadgesVariants(), className)}>
      <h3 className="font-display text-section-subtitle font-bold leading-section-subtitle text-semantic-text-primary">
        {SIMULATION_LABELS.CERTIFICATIONS_COMPLETED}
      </h3>
      <div className="flex items-center gap-2 rounded-lg bg-neutral-white">
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="w-[32px]">
              <SkeletonLoader height="md" width="full" rounded="md" />
            </div>
            <SkeletonLoader height="xs" width="2xl" rounded="sm" />
          </div>
        ) : badges.length === 0 ? (
          <>
            <div className="h-8 w-8 flex-shrink-0">
              <Icon name="Medal" size={32} className="text-neutral-gray-500" />
            </div>
            <p className="font-text text-body-small leading-body-small text-semantic-text-secondary">
              {SIMULATION_LABELS.CERTIFICATIONS_NO_BADGES}
            </p>
          </>
        ) : (
          <div className="flex w-full flex-wrap gap-4">
            {badges?.map((badge, index) => (
              <button
                key={`${badge.certBadgeId}-${index}`}
                aria-label={`View certificate for ${badge.certificationTitle}`}
                onClick={() => handleViewCertificate(badge.certificationId)}
                className={cn(
                  'bg-neutral-gray-000',
                  'px-3 py-2',
                  'rounded-[4px]',
                  'w-full',
                  'border-2 border-transparent focus-visible:border-brand-primary',
                  'outline-none',
                  'hover:bg-neutral-gray-100',
                  'transition-colors duration-200',
                )}
              >
                <span className="3xl:gap-4 pointer-events-none flex items-center gap-2">
                  {badge.iconUrl && !badge.iconUrl.includes('example') ? (
                    <>
                      <div className="3xl:hidden">
                        <AppImage
                          src={badge.iconUrl}
                          alt={badge.certificationTitle}
                          fallbackSrc="/images/fallback-medal.png"
                          className="h-6 w-6"
                          height={26}
                          width={26}
                        />
                      </div>
                      <div className="3xl:block hidden">
                        <AppImage
                          src={badge.iconUrl}
                          alt={badge.certificationTitle}
                          fallbackSrc="/images/fallback-medal.png"
                          className="h-8 w-8"
                          height={32}
                          width={32}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="3xl:hidden">
                        <Icon
                          name="Medal"
                          size={26}
                          className="3xl:hidden text-brand-primary"
                          weight="fill"
                        />
                      </div>
                      <div className="3xl:block hidden">
                        <Icon
                          name="Medal"
                          size={32}
                          className="3xl:block hidden text-brand-primary"
                          weight="fill"
                        />
                      </div>
                    </>
                  )}
                  <span className="3xl:text-body font-text text-body-small font-medium text-semantic-text-primary">
                    {badge.certificationTitleShort || badge.certificationTitle}
                  </span>
                  <span className="ml-auto">
                    <Icon name="ArrowSquareIn" size={24} className="text-neutral-black" />
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

UserBadges.displayName = 'UserBadges';

export default UserBadges;
